import { AfterViewInit, Component, ElementRef, forwardRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';

import { 
    ControlValueAccessor, 
    NG_VALUE_ACCESSOR, 
    Validator, 
    NG_VALIDATORS 
} from '@angular/forms';
import { PGUtilities } from '../../utilities/pg-utilities';

type CodemirrorApi = {
    EditorView: typeof import('codemirror').EditorView;
    basicSetup: typeof import('codemirror').basicSetup;
    EditorState: typeof import('@codemirror/state').EditorState;
    json: typeof import('@codemirror/lang-json').json;
};

@Component({
    selector: 'app-pg-json-editor',
    templateUrl: './pg-json-editor.component.html',
    styleUrls: ['./pg-json-editor.component.scss'],
    providers: [{
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => PgJsonEditorComponent),
            multi: true
        }, {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => PgJsonEditorComponent),
            multi: true,
        }],
    standalone: false
})
export class PgJsonEditorComponent implements OnInit, OnDestroy, ControlValueAccessor, Validator, AfterViewInit {

    @Input() fieldId:string;
    
    @Input() readonly:boolean;
    @Input() required:boolean;

    @Input() jsonType:string;

    value:string = null;

    isLoading = false;

    constructor() { }

    ngOnInit(): void {
    }

    private static _loadPromise: Promise<CodemirrorApi> = null;

    private static loadCodemirror(): Promise<CodemirrorApi> {
        if(PgJsonEditorComponent._loadPromise == null) {
            PgJsonEditorComponent._loadPromise = Promise.all([
                import('codemirror'),
                import('@codemirror/state'),
                import('@codemirror/lang-json'),
            ]).then(([cm, state, langJson]) => ({
                EditorView: cm.EditorView,
                basicSetup: cm.basicSetup,
                EditorState: state.EditorState,
                json: langJson.json,
            }));
        }

        return PgJsonEditorComponent._loadPromise;
    }

    private _cm:CodemirrorApi = null;
    private _editor:InstanceType<CodemirrorApi['EditorView']> = null;
    private _destroyed = false;

    @ViewChild('codeMirrorContainer') codeMirrorContainer:ElementRef;

    ngAfterViewInit(): void {
        this.isLoading = true;

        PgJsonEditorComponent.loadCodemirror().then((cm) => {
            if(this._destroyed) return;

            this._cm = cm;
            this.isLoading = false;

            this._editor = new cm.EditorView({
                parent: this.codeMirrorContainer.nativeElement,
            });

            this._setEditorState();
        }, () => {
            if(this._destroyed) return;
            this.isLoading = false;
        });
    }

    ngOnDestroy(): void {
        this._destroyed = true;

        if(this._editor != null) {
            this._editor.destroy();
            this._editor = null;
        }
    }

    private _invalid = false;

    private _setEditorState() {
        this._invalid = false;
        if(this._editor != null && this._cm != null) {
            const { EditorState, EditorView, basicSetup, json } = this._cm;

            this._editor.setState(EditorState.create({
                doc: JSON.stringify(PGUtilities.tryParseJSON(this.value), null, 4),
                extensions: [
                    basicSetup, 
                    json(),  
                    EditorView.lineWrapping,
                    EditorView.updateListener.of((update) => {
                        let _val = '';

                        for(let _line of update.state.doc) {
                            _val +=_line
                        }

                        try {
                            _val = JSON.stringify(JSON.parse(_val))

                            if(_val != this.value) {
                                this.value = _val;
                                this._invalid = false;
                                this.onValueChange();
                            }
                        }
                        catch(ex) {
                            this._invalid = true
                        }
                    })
                ],
            }))
        }
    }

    onValueChange() {
        if(this._onTouched != null) this._onTouched();
        if(this._onChange != null) this._onChange(this.value);
    }

    // INTERFACCIA ControlValueAccessor

    writeValue(obj: any) {
        this.value = obj;
        this._setEditorState();
    }

    _onChange;

    registerOnChange(fn: any) {
        this._onChange = fn;
    }

    _onTouched;

    registerOnTouched(fn: any) {
        this._onTouched = fn;
    }

    // INTERFACCIA Validator

    validate() {
        if(this.required) {
            if(this.value == '' || this.value == null) return {
                required: {
                    valid: false
                }
            }
        }

        if(this._invalid) {
            return {
                format: {
                    valid: false
                }
            }
        }
    };
}
